import React, { FC, useMemo, useContext, useState } from 'react';
import styles from './index.module.scss';
import CardBox from '../../CardBox';
import { Button, Empty } from '@maxtropy/components';
import { DoubleRightOutlined } from '@ant-design/icons';
import { Modal, Spin } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { apiV2PaintingDashboardWorkshopSummaryPost } from '@maxtropy/device-customer-apis-v2';
import { keepOneAndWanOrLine } from '@/utils/utils';
import { DateContext, ShareDataWrapperContext } from '../../../index';
import { IndexTimeActive } from '../../SwitchDate';
import iconElectricity from '../../../imgs/icon_electricity.png';
import iconCompressedAir from '../../../imgs/icon_electricity_workshop.png';
import iconNaturalGas from '../../../imgs/icon_natural_gas_workshop.png';
import classNames from 'classnames';
import WorkshopDetailModal from './WorkshopDetailModal';

const WorkshopStats: FC = () => {
  const { activeKey, selectedDate } = useContext(DateContext);
  const { isStandardSize } = useContext(ShareDataWrapperContext);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop] = useState<{ name: string; code: number }>({ name: '', code: 0 });

  const requestParams = useMemo(() => {
    const timeResolution = activeKey === IndexTimeActive.THIS_MONTH ? 'M' : 'Y';
    const unit = activeKey === IndexTimeActive.THIS_MONTH ? 'month' : 'year';
    const startTime = selectedDate.startOf(unit).valueOf();
    const now = dayjs();
    const isCurrentPeriod =
      selectedDate.format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY') ===
      now.format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY');
    const endTime = isCurrentPeriod
      ? now.subtract(1, 'day').endOf('day').valueOf()
      : selectedDate.endOf(unit).valueOf();
    return { startTime, endTime, timeResolution };
  }, [activeKey, selectedDate]);

  const { data, loading } = useRequest(() => apiV2PaintingDashboardWorkshopSummaryPost(requestParams), {
    refreshDeps: [requestParams],
  });

  const workshopList = useMemo(() => {
    if (!data?.summaries?.length) return [];
    const chineseNumbers = ['一', '二', '三', '四', '五', '六', '七', '八', '九'];
    return data.summaries.map(item => ({
      name: item.workshop ? `涂装${chineseNumbers[item.workshop - 1] ?? '--'}期` : '--',
      code: item.workshop ?? 1,
      electricity: keepOneAndWanOrLine(item.electricity),
      compressedAir: keepOneAndWanOrLine(item.air),
      naturalGas: keepOneAndWanOrLine(item.gas),
    }));
  }, [data]);

  const renderWorkshopContent = (isHorizontal: boolean) => (
    <div className={classNames(styles.workshop_list, isHorizontal && styles.workshop_list_horizontal)}>
      {workshopList.map((phase, idx) => (
        <div key={idx} className={classNames(styles.workshop_item, isHorizontal && styles.workshop_item_horizontal)}>
          <div className={styles.workshop_header}>
            <span className={styles.workshop_name}>{phase.name}</span>
            <Button
              type="link"
              style={{ padding: 0, marginLeft: 8 }}
              onClick={() => {
                setSelectedWorkshop({ name: phase.name, code: phase.code });
                setDetailModalVisible(true);
              }}
            >
              详情
              <DoubleRightOutlined />
            </Button>
          </div>
          <div className={styles.workshop_metrics}>
            {[
              { icon: iconElectricity, label: '用电量(kWh)', value: phase.electricity },
              { icon: iconCompressedAir, label: '压缩空气(m³)', value: phase.compressedAir },
              { icon: iconNaturalGas, label: '天然气(m³)', value: phase.naturalGas },
            ].map((item, i) => (
              <div key={i} className={styles.metric_card}>
                <div className={styles.metric_top}>
                  <img className={styles.metric_icon} src={item.icon} alt={item.label} />
                  <div className={styles.metric_info}>
                    <span className={styles.metric_label}>{item.label}</span>
                    <div className={styles.metric_value}>{item.value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderModal = () => (
    <Modal
      title={`${selectedWorkshop.name}用能详情`}
      open={detailModalVisible}
      onCancel={() => setDetailModalVisible(false)}
      footer={null}
      width={1400}
      centered
      destroyOnClose
      className={styles.workshopDetailModal}
      getContainer={() => document.getElementById('full_screen_box') ?? document.body}
      bodyStyle={{ padding: 0 }}
    >
      <WorkshopDetailModal workshopCode={selectedWorkshop.code} />
    </Modal>
  );

  // 大屏布局
  if (!isStandardSize) {
    if (loading) {
      return (
        <div className={styles.loading_wrapper}>
          <Spin />
        </div>
      );
    }
    if (!workshopList.length) return <Empty />;
    return (
      <>
        {renderWorkshopContent(true)}
        {renderModal()}
      </>
    );
  }

  // 标准布局
  if (loading) {
    return (
      <CardBox title="车间用量数据" titleType="simple">
        <div className={styles.loading_wrapper}>
          <Spin />
        </div>
      </CardBox>
    );
  }
  if (!workshopList.length) {
    return (
      <CardBox title="车间用量数据" titleType="simple">
        <Empty />
      </CardBox>
    );
  }

  return (
    <CardBox title="车间用量数据" titleType="simple">
      {renderWorkshopContent(false)}
      {renderModal()}
    </CardBox>
  );
};

export default WorkshopStats;
